// src/components/briefings/BriefingProgressToast.tsx
import React, { useEffect, useState, useRef } from 'react'
import { X, CheckCircle, AlertCircle, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { briefings } from '@/lib/api'

interface ProgressData {
  job_id?: string
  status: 'queued' | 'processing' | 'success' | 'failed' | 'none'
  stage: string
  progress: number
  message: string
  briefing_id?: number
  article_count?: number
  error?: string
}

interface FakeStage {
  stage: string
  progress: number
  message: string
  duration: number
}

const FAKE_STAGES: FakeStage[] = [
  { stage: 'queued', progress: 0, message: 'Queued for processing', duration: 500 },
  { stage: 'fetching', progress: 10, message: 'Fetching sources...', duration: 2000 },
  { stage: 'deduplicating', progress: 20, message: 'Removing duplicates...', duration: 1500 },
  { stage: 'enriching', progress: 30, message: 'Enriching content...', duration: 2000 },
  { stage: 'polari_categorization', progress: 40, message: 'Polari Layer 0: Quality Filtering', duration: 1500 },
  { stage: 'polari_clustering', progress: 50, message: 'Polari Layer 1: Story Clustering', duration: 1500 },
  { stage: 'polari_trends', progress: 60, message: 'Polari Layer 2: Trend Intelligence', duration: 1500 },
  { stage: 'polari_final', progress: 70, message: 'Polari Layer 3: Final Analysis', duration: 1500 },
  { stage: 'organizing', progress: 75, message: 'Structuring content...', duration: 1000 },
  { stage: 'generating', progress: 85, message: 'Generating AI summary...', duration: 3000 },
  { stage: 'finalizing', progress: 95, message: 'Building briefing...', duration: 1000 },
]

export function BriefingProgressToast() {
  const navigate = useNavigate()
  const [dismissed, setDismissed] = useState(false)
  const [fakeProgress, setFakeProgress] = useState<Partial<ProgressData>>({
    stage: 'queued',
    progress: 0,
    message: 'Queued for processing'
  })
  const timeoutRef = useRef<number | null>(null)
  const currentStageIndexRef = useRef(0)

  const { data: progress, isError } = useQuery<ProgressData>({
    queryKey: ['activeGenerationStatus'],
    queryFn: async () => {
      try {
        return await briefings.getActiveGenerationStatus()
      } catch (error: any) {
        console.log('Generation status error:', error)
        return {
          status: 'none' as const,
          stage: '',
          progress: 0,
          message: ''
        }
      }
    },
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data || data.status === 'success' || data.status === 'failed' || data.status === 'none') {
        return false
      }
      return 2000
    },
    enabled: !dismissed,
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 0
  })

  // Start fake progress when generation begins
  useEffect(() => {
    console.log('Progress status:', progress?.status)
    if (progress?.status === 'processing' || progress?.status === 'queued') {
      console.log('Starting fake progress')
      startFakeProgress()
    }
  }, [progress?.status, progress?.job_id])

  const startFakeProgress = () => {
    // Reset if new job
    currentStageIndexRef.current = 0
    updateNextStage()
  }

  const updateNextStage = () => {
    if (currentStageIndexRef.current < FAKE_STAGES.length) {
      const stage = FAKE_STAGES[currentStageIndexRef.current]

      setFakeProgress({
        stage: stage.stage,
        progress: stage.progress,
        message: stage.message
      })

      timeoutRef.current = setTimeout(() => {
        currentStageIndexRef.current++
        updateNextStage()
      }, stage.duration)
    }
  }

  const stopFakeProgress = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    currentStageIndexRef.current = 0
  }

  // Reset dismissed state when new generation starts
  useEffect(() => {
    if (progress?.job_id && progress.status === 'queued') {
      setDismissed(false)
    }
  }, [progress?.job_id])

  // Don't render if no active generation or dismissed
  if (!progress || progress.status === 'none' || dismissed) {
    return null
  }

  // Use fake progress overlay when processing
  const displayProgress =
    (progress.status === 'processing' || progress.status === 'queued') &&
      fakeProgress.progress !== undefined &&
      fakeProgress.progress >= (progress.progress || 0)
      ? {
        ...progress,
        stage: fakeProgress.stage || progress.stage,
        progress: fakeProgress.progress,
        message: fakeProgress.message || progress.message
      }
      : progress

  const getStageIcon = () => {
    if (displayProgress.status === 'success') {
      return <CheckCircle className="w-5 h-5 text-green-500" />
    }
    if (displayProgress.status === 'failed') {
      return <AlertCircle className="w-5 h-5 text-red-500" />
    }
    return <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
  }

  const getStageText = () => {
    const stages: Record<string, string> = {
      queued: 'Queued',
      initializing: 'Initializing',
      fetching: 'Fetching Sources',
      deduplicating: 'Deduplicating Articles',
      enriching: 'Enriching Content',
      scoring: 'Calculating Importance',
      filtering: 'Quality Filtering',
      polari_categorization: 'Polari Layer 0: Quality Filtering',
      polari_clustering: 'Polari Layer 1: Story Clustering',
      polari_trends: 'Polari Layer 2: Trend Intelligence',
      polari_final: 'Polari Layer 3: Final Analysis',
      ranking: 'Ranking Articles',
      organizing: 'Structuring Content',
      trends: 'Fetching Trend Data',
      generating: 'Generating AI Summary',
      finalizing: 'Building Briefing',
      complete: 'Complete',
      error: 'Failed'
    }
    return stages[displayProgress.stage] || displayProgress.stage
  }

  const isPolariPhase = () => {
    return displayProgress.stage?.startsWith('polari_')
  }

  const handleViewBriefing = () => {
    if (displayProgress.briefing_id) {
      navigate(`/app/briefings/${displayProgress.briefing_id}`)
      setDismissed(true)
    }
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 max-w-[calc(100vw-3rem)]">
      <div className={`bg-white dark:bg-gray-900 rounded-lg p-4 shadow-2xl border-l-4 animate-slide-up ${isPolariPhase()
        ? 'border-l-pink-500 dark:border-l-pink-400 polari-glow'
        : 'border-l-blue-500 dark:border-l-blue-400'
        }`}>
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            {getStageIcon()}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                {displayProgress.status === 'success' ? 'Briefing Ready!' : 'Generating Briefing'}
              </h4>
              <p className={`text-xs ${isPolariPhase() ? 'text-pink-600 dark:text-pink-400 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>
                {getStageText()}
              </p>
            </div>
          </div>
          <button
            onClick={() => setDismissed(true)}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Progress Bar */}
        {displayProgress.status !== 'failed' && (
          <div className="mb-3">
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-500 ease-out ${isPolariPhase()
                  ? 'bg-gradient-to-r from-pink-500 to-pink-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
                  }`}
                style={{ width: `${displayProgress.progress}%` }}
              />
            </div>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{displayProgress.message}</p>
          </div>
        )}

        {/* Error Message */}
        {displayProgress.status === 'failed' && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
            <p className="text-sm text-red-800 dark:text-red-200">{displayProgress.error || displayProgress.message}</p>
          </div>
        )}

        {/* Success Info */}
        {displayProgress.status === 'success' && displayProgress.article_count && (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 mb-3">
            <p className="text-sm text-green-800 dark:text-green-200">
              ✓ {displayProgress.article_count} articles processed
            </p>
          </div>
        )}

        {/* Actions */}
        {displayProgress.status === 'success' && (
          <button
            onClick={handleViewBriefing}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded-lg text-sm font-medium transition-colors"
          >
            View Briefing →
          </button>
        )}
      </div>
    </div>
  )
}